import { Button, DropdownMenu } from '@components'
import { useBusinessContext } from '@context'
import { useQueryClient } from '@tanstack/react-query'
import { IDocument } from '@query/documents'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaCircleCheck, FaFileInvoice } from 'react-icons/fa6'
import { useTheme } from 'styled-components'

import {
  buildOptimisticDocumentRelation,
  getLinkKindRelationRole,
  getLinkKindRelationType,
  getRelationsForLinkKind,
  IRelationLinkKindMeta,
  RelationLinkKind
} from './relationMapperUtils'
import { DocumentRelationFormValue } from './RelationRow'

type UseRelationMapperModalParams = {
  relations: DocumentRelationFormValue[]
  handleChange: (relations: DocumentRelationFormValue[]) => void
  effectiveReadOnly: boolean
  availableLinkKinds: RelationLinkKind[]
  availableLinkKindMetas?: IRelationLinkKindMeta[] | null
}

export const useRelationMapperModal = ({
  relations,
  handleChange,
  effectiveReadOnly,
  availableLinkKinds,
  availableLinkKindMetas = null
}: UseRelationMapperModalParams) => {
  const { t } = useTranslation()
  const { slug } = useBusinessContext()
  const queryClient = useQueryClient()
  const theme = useTheme()

  const [isModalOpen, setModalOpen] = useState(false)
  const [modalLinkKind, setModalLinkKind] = useState<RelationLinkKind>('salesInvoice')
  const [modalSelection, setModalSelection] = useState<number[]>([])
  const [modalInitialSelection, setModalInitialSelection] = useState<number[]>([])

  const openModalWithKind = (kind: RelationLinkKind) => {
    if (effectiveReadOnly) return
    const existingIds = getRelationsForLinkKind(relations, kind, availableLinkKindMetas).map(
      relation => relation.related_document
    )
    setModalInitialSelection(existingIds)
    setModalSelection(existingIds)
    setModalLinkKind(kind)
    setModalOpen(true)
  }

  const handleCloseModal = () => {
    setModalSelection([])
    setModalInitialSelection([])
    setModalOpen(false)
  }

  const handleConfirmSelection = () => {
    const role = getLinkKindRelationRole(modalLinkKind, availableLinkKindMetas)
    const relationType = getLinkKindRelationType(modalLinkKind, availableLinkKindMetas)
    const existingByDocId = new Map(
      getRelationsForLinkKind(relations, modalLinkKind, availableLinkKindMetas).map(relation => [
        relation.related_document,
        relation
      ])
    )
    const otherRelations = relations.filter(
      relation => relation.role !== role || relation.type !== relationType
    )

    const updatedRelations: DocumentRelationFormValue[] = modalSelection.map(
      (relatedDocumentId, index) => {
        const existing = existingByDocId.get(relatedDocumentId)
        if (existing) {
          return existing
        }

        const cachedDocument = queryClient.getQueryData<IDocument>([
          slug,
          'documents',
          relatedDocumentId
        ])

        return buildOptimisticDocumentRelation(
          {
            related_document: relatedDocumentId,
            role,
            type: relationType
          },
          { cachedDocument, optimisticIdSuffix: index }
        )
      }
    )

    handleChange([...otherRelations, ...updatedRelations])
    handleCloseModal()
  }

  const quickActionButtons = availableLinkKinds.map(kind => {
    switch (kind) {
      case 'payment':
        return (
          <Button
            key={kind}
            type="button"
            icon={<FaCircleCheck color={theme.colors.success} />}
            onClick={() => openModalWithKind(kind)}
          >
            {t('documentRelations.quickActions.paymentAddInvoiceEntry')}
          </Button>
        )
      case 'salaryPayment':
        return (
          <Button
            key={kind}
            type="button"
            icon={<FaCircleCheck color={theme.colors.success} />}
            onClick={() => openModalWithKind(kind)}
          >
            {t('documentRelations.quickActions.salaryPayment')}
          </Button>
        )
      case 'salesInvoice':
      case 'purchaseInvoice':
      case 'salary':
        return (
          <Button
            key={kind}
            type="button"
            icon={<FaFileInvoice color={theme.colors.dashboardNeg} />}
            onClick={() => openModalWithKind(kind)}
          >
            {t(`documentRelations.quickActions.${kind}`)}
          </Button>
        )
      default: {
        const exhaustive: never = kind
        return exhaustive
      }
    }
  })

  const renderQuickActionMenu = (trigger: React.ReactElement): React.ReactNode => {
    if (effectiveReadOnly || quickActionButtons.length === 0) {
      return null
    }

    return <DropdownMenu items={quickActionButtons}>{trigger}</DropdownMenu>
  }

  return {
    isModalOpen,
    modalLinkKind,
    modalSelection,
    modalInitialSelection,
    setModalSelection,
    handleConfirmSelection,
    handleCloseModal,
    renderQuickActionMenu
  }
}
